import axios from 'axios';
import { buildApiUrl } from '../../services/apiConfig';
import { COLORS, CONSTANT_COLORS } from './constants';

const API_BASE_URL = buildApiUrl('AUTH');
const CACHE_DURATION = 3600000; 
/**
 * Service for managing application colors
 */
class ColorService {
  /**
   * Apply color settings to CSS variables
   * @param {Object} colorObj - The color object to apply
   */
  applyColors(colorObj) {
    const root = document.documentElement;
    
    Object.entries(colorObj).forEach(([key, value]) => {
      if (value) {
        root.style.setProperty(`--color-${key.toLowerCase().replace(/_/g, '-')}`, value);
      }
    });
    
    this.applyConstantColors();
  }

  /**
   * Apply constant colors that should never change
   */
  applyConstantColors() {
    const root = document.documentElement;
    
    Object.entries(CONSTANT_COLORS).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key.toLowerCase().replace(/_/g, '-')}`, value);
    });
  }

  /**
   * Get default colors
   * @returns {Object} - The default colors
   */
  getDefaultColors() {
    return COLORS;
  }

  /**
   * Fetch user color preferences from the API
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - The color preferences or null if not found
   */
  async getUserColorPreferences(userId) {
    try {
      if (!userId) return null;

      const response = await axios.get(`${API_BASE_URL}/api/user/color-preferences`, {
        headers: { 'user-id': userId }
      });

      if (response.data.preferences) {
        return {
          PRIMARY: response.data.preferences.primary_color,
          PRIMARY_LIGHT: response.data.preferences.primary_light,
          PRIMARY_DARK: response.data.preferences.primary_dark,
          SECONDARY: response.data.preferences.secondary_color,
          SECONDARY_LIGHT: response.data.preferences.secondary_light,
          SECONDARY_DARK: response.data.preferences.secondary_dark
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching user color preferences:', error);
      return null;
    }
  }

  /**
   * Get user color preferences with caching
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - The color preferences
   */
  async getUserColorPreferencesWithCache(userId) {
    if (!userId) return null;
    
    const cacheKey = `colorPrefs_${userId}`;
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      try {
        const { colors, timestamp } = JSON.parse(cachedData);
        if (timestamp && (Date.now() - timestamp < CACHE_DURATION)) {
          return colors;
        }
      } catch (e) {
        console.error('Error parsing cached colors:', e);
      }
    }

    const colors = await this.getUserColorPreferences(userId);

    if (colors) {
      localStorage.setItem(cacheKey, JSON.stringify({
        colors,
        timestamp: Date.now()
      }));
    }

    return colors;
  }

  /**
   * Save user color preferences to the API
   * @param {string} userId - The user ID
   * @param {Object} colorData - The color data to save
   * @returns {Promise<boolean>} - Whether the save was successful
   */
  async saveUserColorPreferences(userId, colorData) {
    try {
      if (!userId) return false;

      const payload = {
        primary_color: colorData.PRIMARY || colorData.primary_color,
        primary_light: colorData.PRIMARY_LIGHT || colorData.primary_light,
        primary_dark: colorData.PRIMARY_DARK || colorData.primary_dark,
        secondary_color: colorData.SECONDARY || colorData.secondary_color,
        secondary_light: colorData.SECONDARY_LIGHT || colorData.secondary_light,
        secondary_dark: colorData.SECONDARY_DARK || colorData.secondary_dark
      };

      await axios.post(`${API_BASE_URL}/api/user/color-preferences`, payload, {
        headers: {
          'user-id': userId,
          'Content-Type': 'application/json'
        }
      });

      this.clearColorCache(userId);
      
      return true;
    } catch (error) {
      console.error('Error saving user color preferences:', error);
      return false;
    }
  }

  /**
   * Clear color cache for a user
   * @param {string} userId - The user ID
   */
  clearColorCache(userId) {
    if (userId) {
      localStorage.removeItem(`colorPrefs_${userId}`);
    }
  }

  /**
   * Refresh user colors
   * @param {string} userId - The user ID
   * @returns {Promise<boolean>} - Whether the refresh was successful
   */
  async refreshUserColors(userId) {
    this.clearColorCache(userId);
    
    if (userId) {
      const colors = await this.getUserColorPreferences(userId);
      if (colors) {
        this.applyColors(colors);
        return true;
      }
    }
    
    this.applyColors(this.getDefaultColors());
    return false;
  }
}

export default new ColorService();